"use client";
import { ProductFilter } from "@lib/data/products";
import { useProgressRouter } from "@lib/util/navigation";
import { Checkbox } from "@medusajs/ui";
import { useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";

type Props = {
  value: ProductFilter[];
  filterKey: string;
};

const FilterNameCheckBox = ({ value, filterKey }: Props) => {
  const router = useProgressRouter();
  const searchParams = useSearchParams();

  const productInfo = useMemo(() => {
    return value.map((item) => ({
      value: item.id,
      label: item.name ?? item.title,
      count: item.total_product,
    }));
  }, [JSON.stringify(value), filterKey]);

  const checkedItems = useMemo(() => {
    const paramValues = searchParams.get(convertFilterKey(filterKey))?.split(",") || [];
    return Object.fromEntries(productInfo.map((item) => [item.value, paramValues.includes(item.value)]));
  }, [productInfo, filterKey, searchParams]);

  const handleCheckboxChange = (val: string) => {
    const updated = {
      ...checkedItems,
      [val]: !checkedItems[val],
    };

    const selected = Object.entries(updated)
      .filter(([_, value]) => value)
      .map(([id]) => id);

    const key = convertFilterKey(filterKey);

    const params = new URLSearchParams(Array.from(searchParams.entries()));
    params.delete("page");

    if (selected.length > 0) {
      params.set(key, selected.join(","));
    } else {
      params.delete(key);
    }

    const hasCategoryIds = params.has("category_ids");

    if (!hasCategoryIds) {
      router.replace(window.location.pathname, { scroll: false });
    }

    router.replace(`?${decodeURIComponent(params.toString())}`, {
      scroll: false,
    });
  };

  const visibleItems = productInfo.length > 4 ? productInfo.slice(0, 4) : productInfo;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="text-small-regular py-2">
      <div className="grid grid-cols-1 gap-x-8 gap-y-2">
        {(expanded ? productInfo : visibleItems).map((item, index) => (
          <label key={index} className="inline-flex items-center cursor-pointer w-fit">
            <Checkbox
              id={`checkbox-${item.value}`}
              checked={checkedItems[item.value]}
              onCheckedChange={() => handleCheckboxChange(item.value)}
            />
            <span className="text-[14px] pt-1 pl-2">
              {item.label} ({item.count ?? 0})
            </span>
          </label>
        ))}
      </div>
      {productInfo.length > 4 && (
        <button onClick={() => setExpanded(!expanded)} className="mt-2 text-primary-500 hover:underline">
          {expanded ? "Thu gọn" : "Xem thêm"}
        </button>
      )}
    </div>
  );
};

const convertFilterKey = (key: string) => {
  switch (key) {
    case "categories":
      return "category_ids";
    case "brands":
      return "brand_ids";
    case "collections":
      return "collection_ids";
    default:
      return `attribute_value_ids.${key}`;
  }
};

export default FilterNameCheckBox;
